import {
  DirectionsBoatIcon,
  DirectionsRailwayIcon,
  LocalAirportIcon,
  PersonPinCircleSharpIcon,
  PlaceIcon,
  SeaAirIcon,
} from '@freighthub/silhouette-core'
import {
  Address,
  AutocompleteResult,
  formatAddress,
  GooglePlace,
  isAddress,
  isLocation,
  isPort,
  PlacesData,
  PlacesSearchType,
  placeToString,
} from 'features/Directory'
import { ComponentType } from 'react'

import { TPlaceData } from '../fromMobx'

type AddressIcon = PlacesSearchType | 'location' | 'seaAir'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
const isGooglePlace = (place: any): place is GooglePlace => {
  if (typeof place !== 'object') return false
  return place.place_id && place.description
}

const placeTypePropertiesMap: Record<AddressIcon | 'location', ComponentType> = {
  seaAir: SeaAirIcon,
  rail: DirectionsRailwayIcon,
  sea: DirectionsBoatIcon,
  air: LocalAirportIcon,
  address: PlaceIcon,
  location: PersonPinCircleSharpIcon,
}

/**
 * This function checks, if the params has sea | rail | air | both and returns a single response.
 * @param types {Array} | types of transport mode selected by user or types of transport mode available for a location.
 * @returns {string} | return the mode
 */
export const getTpModeByPlaceTypes = (types) => {
  const transportModeSet = new Set(types)

  return transportModeSet.has('sea') && transportModeSet.has('air')
    ? 'seaAir'
    : transportModeSet.has('sea')
      ? 'sea'
      : transportModeSet.has('air')
        ? 'air'
        : transportModeSet.has('rail')
          ? 'rail'
          : ''
}

/**
 * We compared the user selected transport mode and the transport mode available on a particular location of search results.
 * Based on the comparison, we sent the respective icons to be displayed.
 * @param types {Array} | Array of places that has ports and address
 * @param selectedTransportMode {string} |
 * @returns
 */
const getIconByMode = (types, selectedTransportMode) => {
  const indexOfSelectedTpMode = types.indexOf(selectedTransportMode)
  if (indexOfSelectedTpMode === -1) {
    const placeIconType = getTpModeByPlaceTypes(types)
    return placeTypePropertiesMap[placeIconType] || PlaceIcon
  }

  return placeTypePropertiesMap[selectedTransportMode]
}

export function getPlaceIcon(place: TPlaceData | Address, selectedTransportMode?: string): ComponentType {
  if (isLocation(place)) return placeTypePropertiesMap.location
  if (isGooglePlace(place)) return placeTypePropertiesMap.address
  if (selectedTransportMode) return getIconByMode(place.types, selectedTransportMode)
  return PlaceIcon
}

export function getPlaceId(place: TPlaceData | Address): string {
  return isGooglePlace(place) ? place.place_id : place._id
}

export function formatPlaceLabel(
  placeData: TPlaceData | Address,
  selectedTransportMode?: string,
  isTPPortsEnabled?: boolean
): string {
  if (isGooglePlace(placeData)) return placeData.description
  if (isAddress(placeData)) {
    return formatAddress(placeData)
  }
  if (isLocation(placeData)) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${placeData.name}, ${placeData.companyName ? `${placeData.companyName},` : ''} ${placeToString(
      placeData.place
    )}`
  }
  if (isPort(placeData)) {
    const { _id, iataCode } = placeData
    if (isTPPortsEnabled) {
      if (selectedTransportMode === 'air' || selectedTransportMode === 'seaAir') {
        const portReference = placeData.types.includes('air') ? iataCode : _id
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `${placeToString(placeData, false)}${portReference ? `, ${portReference}` : ''}`.trim()
      }
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${placeToString(placeData, false)}${_id ? `, ${_id}` : ''}`.trim()
  }
  return ''
}

export function placeToAutocompleteResult(
  place: TPlaceData | Address,
  selectedTransportMode?: string,
  isTPPortsEnabled?: boolean
): AutocompleteResult {
  const PlaceIconComponent = getPlaceIcon(place, selectedTransportMode)

  return {
    value: getPlaceId(place),
    label: formatPlaceLabel(place, selectedTransportMode, isTPPortsEnabled),
    data: {
      _id: 'label',
      entity: place,
      icon: <PlaceIconComponent />,
    },
  }
}

export function formatResults(
  places: PlacesData,
  selectedTransportMode?: string,
  isTPPortsEnabled?: boolean
): AutocompleteResult[] {
  return [
    ...places.ports.map((place) => placeToAutocompleteResult(place, selectedTransportMode, isTPPortsEnabled)),
    ...places.addresses.map((place) => placeToAutocompleteResult(place, selectedTransportMode, isTPPortsEnabled)),
    ...places.locations.data.map((place) => placeToAutocompleteResult(place, selectedTransportMode, isTPPortsEnabled)),
  ]
}

export const filterResults = (results: PlacesData, query: string, maxNumber: number): PlacesData => {
  const portAndAddressMaxNumber = query.length ? maxNumber : 0
  return {
    ...results,
    addresses: results.addresses.slice(0, portAndAddressMaxNumber),
    ports: results.ports.slice(0, portAndAddressMaxNumber),
    locations: {
      ...results.locations,
      data: results.locations.data.slice(0, maxNumber),
    },
  }
}
